import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { useSubscription } from "../contexts/SubscriptionProvider";
import { motion } from "framer-motion";
import {
  CheckCircle2, XCircle, Loader2, Crown, Sparkles, TrendingUp, Star, Settings2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const TIER_ICONS = { consultor: TrendingUp, profesional: Crown, empresarial: Crown };
const TIER_COLORS = { consultor: "#3B82F6", profesional: "#10B981", empresarial: "#A855F7" };

const FEATURE_LABELS = {
  multi_tenant: { es: "Multi-Tenant (varios clientes)", en: "Multi-Tenant (multiple clients)" },
  engagements: { es: "Gestion de Proyectos", en: "Engagements / Projects" },
  client_dashboard: { es: "Ficha del Cliente", en: "Client Dashboard" },
  pdf_export: { es: "Exportacion PDF", en: "PDF Export" },
  gap_analysis: { es: "Analisis de Brechas", en: "Gap Analysis" },
  risk_register: { es: "Registro de Riesgos", en: "Risk Register" },
  soa: { es: "SoA", en: "SoA" },
  incidents: { es: "Incidentes", en: "Incidents" },
  ai_policy_generator: { es: "AI Generador de Politicas", en: "AI Policy Generator" },
  ai_risk_predictor: { es: "AI Predictor de Riesgos", en: "AI Risk Predictor" },
  ai_implementation_wizard: { es: "AI Asistente de Implementacion", en: "AI Implementation Wizard" },
  evidence_cloud_connectors: { es: "Conectores Cloud (AWS/Azure/GCP)", en: "Cloud Connectors (AWS/Azure/GCP)" },
  executive_report: { es: "Reporte Ejecutivo", en: "Executive Report" },
  vendor_risk: { es: "Riesgo de Proveedores", en: "Vendor Risk" },
  threat_intelligence: { es: "Threat Intelligence", en: "Threat Intelligence" },
  rbac_custom_roles: { es: "RBAC Roles Personalizados", en: "RBAC Custom Roles" },
  smtp_notifications: { es: "Notificaciones SMTP", en: "SMTP Notifications" },
  bulk_import_export: { es: "Import/Export Masivo", en: "Bulk Import/Export" },
  api_access: { es: "Acceso API", en: "API Access" },
  priority_support: { es: "Soporte Prioritario", en: "Priority Support" }
};

const Billing = () => {
  const { token, user } = useAuth();
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const { subscription, refresh } = useSubscription();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState("");

  const fetchTiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/tiers`);
      setTiers(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  const upgrade = async (tierId) => {
    if (user?.role !== "owner") {
      toast.error(lang === "es" ? "Solo el propietario de la organizacion puede cambiar el plan" : "Only the org owner can change the plan");
      return;
    }
    if (tierId === subscription?.tier_id) return;
    const targetTierName = tiers.find(ti => ti.id === tierId)?.name;
    if (!(await confirm({
      title: t("billing.switch_title"),
      description: `${t("billing.switch_desc_prefix")} ${targetTierName}${t("billing.switch_desc_suffix")}`,
    }))) return;
    setUpgrading(tierId);
    try {
      await axios.put(`${API}/subscription/upgrade?tier=${tierId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "es" ? "Plan actualizado" : "Plan updated");
      await refresh();
    } catch (e) {
      toast.error(extractError(e, "Failed"));
    } finally { setUpgrading(""); }
  };

  const startTrial = async (tier) => {
    if (user?.role !== "owner") {
      toast.error(lang === "es" ? "Solo el propietario" : "Only the org owner");
      return;
    }
    setUpgrading(tier.id);
    try {
      const res = await axios.post(
        `${API}/subscription/start-trial?tier=${tier.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(lang === "es"
        ? `Prueba de ${res.data.trial_days} dias activada`
        : `${res.data.trial_days}-day trial started`);
      await refresh();
    } catch (e) {
      toast.error(extractError(e, "Failed"));
    } finally { setUpgrading(""); }
  };

  // Sprint 4E: PayPal flow — create order then open approval URL
  const payWithPayPal = async (tier) => {
    if (user?.role !== "owner") {
      toast.error(lang === "es" ? "Solo el propietario puede pagar" : "Only the org owner can pay");
      return;
    }
    setUpgrading(tier.id);
    try {
      const cfgRes = await axios.get(`${API}/billing/paypal/config`);
      if (!cfgRes.data?.configured) {
        toast.error(lang === "es" ? "PayPal no esta configurado" : "PayPal is not configured");
        return;
      }
      const res = await axios.post(`${API}/billing/paypal/create-order`,
        { tier_id: tier.id, amount: tier.price_monthly, currency: "USD" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const env = cfgRes.data.environment === "live" ? "" : ".sandbox";
      const url = `https://www${env}.paypal.com/checkoutnow?token=${res.data.order_id}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(lang === "es"
        ? "Aprueba el pago en PayPal y vuelve aqui"
        : "Approve in PayPal and return here");
    } catch (e) {
      toast.error(extractError(e, "PayPal failed"));
    } finally { setUpgrading(""); }
  };

  if (loading || !subscription) {
    return <div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const currentTier = subscription.tier_id;

  return (
    <div className="p-6 space-y-6" data-testid="billing-page">
      <PageHeader
        title={lang === "es" ? "Suscripcion y Facturacion" : "Subscription & Billing"}
        description={lang === "es"
          ? "Administra tu plan, limites de uso y caracteristicas activas."
          : "Manage your plan, usage limits and active features."}
      />

      {/* Current Plan + Usage */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{lang === "es" ? "Tu plan actual" : "Your current plan"}</p>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-primary" />
                {subscription.tier.name}
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {subscription.is_trialing && (
                <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600">
                  {lang === "es"
                    ? `Prueba: ${subscription.trial_days_remaining}d restantes`
                    : `Trial: ${subscription.trial_days_remaining}d left`}
                </Badge>
              )}
              <Badge variant="default" className="text-xs">{subscription.status.toUpperCase()}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(subscription.quota_status).map(([key, q]) => {
              const pct = q.unlimited ? 0 : q.limit > 0 ? Math.round((q.used / q.limit) * 100) : 0;
              return (
                <div key={key} className="space-y-1.5" data-testid={`quota-${key}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}</span>
                    <span className={q.exceeded ? "text-red-500 font-bold" : "font-medium"}>
                      {q.used}{q.unlimited ? " / ∞" : ` / ${q.limit}`}
                    </span>
                  </div>
                  {!q.unlimited && <Progress value={pct} className={`h-1.5 ${q.exceeded ? "bg-red-200" : ""}`} />}
                  {q.unlimited && <div className="h-1.5 bg-green-500/20 rounded-full"><div className="h-full bg-green-500 rounded-full" style={{ width: "100%" }} /></div>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* S17b — Framework picker cross-link (ADR-S17b-003) */}
      <Card className="border-border/60">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Settings2 className="w-5 h-5 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium">
                  {lang === "es" ? "Frameworks activos" : "Active frameworks"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {lang === "es"
                    ? "Elige qué marcos normativos usa tu organización."
                    : "Choose which standards your organization uses."}
                </p>
              </div>
            </div>
            <Link to="/settings/frameworks">
              <Button variant="outline" size="sm" className="shrink-0">
                {lang === "es" ? "Gestionar frameworks" : "Manage frameworks"}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map(tier => {
          const Icon = TIER_ICONS[tier.id] || Star;
          const isCurrent = tier.id === currentTier;
          const isUpgrade = tiers.findIndex(t => t.id === tier.id) > tiers.findIndex(t => t.id === currentTier);
          return (
            <motion.div key={tier.id} whileHover={{ scale: 1.02 }}>
              <Card className={`border-border h-full relative ${isCurrent ? "ring-2 ring-primary" : ""}`} data-testid={`tier-${tier.id}`}>
                {isCurrent && <Badge className="absolute -top-2 right-4 text-[10px]">{lang === "es" ? "PLAN ACTUAL" : "CURRENT"}</Badge>}
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Icon className="w-6 h-6" style={{ color: TIER_COLORS[tier.id] }} />
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                  </div>
                  <div className="mt-2">
                    <span className="text-3xl font-heading font-bold">${tier.price_monthly}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{tier.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Limits */}
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "es" ? "Clientes" : "Clients"}</span><b>{tier.limits.client_tenants === -1 ? "∞" : tier.limits.client_tenants}</b></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "es" ? "Usuarios" : "Users"}</span><b>{tier.limits.users === -1 ? "∞" : tier.limits.users}</b></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "es" ? "Riesgos" : "Risks"}</span><b>{tier.limits.risks === -1 ? "∞" : tier.limits.risks}</b></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "es" ? "Proyectos" : "Engagements"}</span><b>{tier.limits.engagements === -1 ? "∞" : tier.limits.engagements}</b></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{lang === "es" ? "Marcos" : "Frameworks"}</span><b>{tier.limits.frameworks}</b></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">AI credits/mo</span><b>{tier.limits.ai_credits_monthly}</b></div>
                  </div>

                  {/* Features */}
                  <div className="space-y-1 pt-3 border-t border-border">
                    {Object.entries(tier.features).map(([flag, enabled]) => (
                      <div key={flag} className="flex items-center gap-2 text-xs">
                        {enabled ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                        )}
                        <span className={enabled ? "" : "text-muted-foreground/60 line-through"}>
                          {FEATURE_LABELS[flag]?.[lang] || flag}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full"
                    variant={isCurrent ? "outline" : isUpgrade ? "default" : "ghost"}
                    disabled={isCurrent || upgrading === tier.id}
                    onClick={() => upgrade(tier.id)}
                    data-testid={`upgrade-${tier.id}`}
                  >
                    {upgrading === tier.id
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{lang === "es" ? "Actualizando..." : "Updating..."}</>
                      : isCurrent
                        ? (lang === "es" ? "Plan actual" : "Current plan")
                        : isUpgrade
                          ? (lang === "es" ? "Actualizar" : "Upgrade")
                          : (lang === "es" ? "Cambiar" : "Switch")}
                  </Button>
                  {!isCurrent && tier.price_monthly > 0 && (
                    <Button
                      variant="outline"
                      className="w-full mt-2"
                      disabled={upgrading === tier.id}
                      onClick={() => payWithPayPal(tier)}
                      data-testid={`paypal-${tier.id}`}
                    >
                      {lang === "es" ? "Pagar con PayPal" : "Pay with PayPal"}
                    </Button>
                  )}
                  {!isCurrent && (tier.trial_days || 0) > 0 && (
                    <Button
                      variant="ghost"
                      className="w-full mt-2"
                      disabled={upgrading === tier.id}
                      onClick={() => startTrial(tier)}
                      data-testid={`trial-${tier.id}`}
                    >
                      {lang === "es"
                        ? `Demo de ${tier.trial_days} dias`
                        : `Start ${tier.trial_days}-day demo`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center pt-4">
        {lang === "es"
          ? "Procesador de pagos: PayPal. La activacion del plan se confirma al recibir la captura del pago."
          : "Payment processor: PayPal. Plan activation is confirmed when the capture event is received."}
      </p>
    </div>
  );
};

export default Billing;
